import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Button, Dropdown, Pagination, Table } from "semantic-ui-react";

// UI
import CardAffiliateSettings from "react-lib/apps/MEH/CardAffiliateSettings";

import moment from "moment";

import DateTextBox from "react-lib/apps/common/DateTextBox";
import ErrorMessage from "react-lib/component-js/common/ErrorMessage";
import SearchBoxDropdown from "react-lib/appcon/common/SearchBoxDropdown";

import {
  AffiliateCommissionFilterType,
  AffiliateCommissionType,
  BTN_ACTS,
  COMMISSION_STATUS_OPTIONS,
  ROWS_PER_PAGE,
  SEQUENCE_NAME,
} from "./sequence/AffiliateCommission";
import { AffiliateSettingsType } from "react-lib/apps/MEH/sequence/AffiliateSettings";

import "./CardAffiliateCommission.scss";

type CardAffiliateCommissionProps = {
  onEvent: any;
  runSequence: any;
  setProp: any;
  AffiliateCommissionSequence?: AffiliateCommissionType;
  AffiliateSettingsSequence?: AffiliateSettingsType;
  buttonLoadCheck?: any;
  errorMessage?: any;
  searchedItemListWithKey?: Record<string, any>;
  masterData?: Record<string, any>;
  masterOptions?: Record<string, any>;
  languageUX?: string;
};

type TabName = "APPROVE" | "PAY" | "SEARCH" | "SETTING";

const CARD_ID = "CardAffiliateCommission";

const TAB_ITEMS: { disabled: boolean; name: TabName; text: string }[] = [
  { disabled: false, name: "SEARCH", text: "Search Commission" },
  { disabled: true, name: "APPROVE", text: "Approve Commission" },
  { disabled: true, name: "PAY", text: "Pay Commission" },
  { disabled: false, name: "SETTING", text: "Affiliate Settings" },
];

const TABLE_HEADERS = [
  "วันที่ทำรายการ",
  "วันที่ชำระเงิน",
  "วันที่ใช้งาน Package",
  "Affiliate Code",
  "ผู้รับ Commission",
  "ผู้รับบริการ",
  "ชื่อ Package",
  "ราคาขาย",
  "Commission Rate",
  "Commission Amount",
  "Status",
];

const BE_DATE_PATTERN = /^\d{2}\/\d{2}\/\d{4}$/;

const formatDisplayDate = (value?: string | null) =>
  value ? moment(value).add(543, "years").format("DD/MM/YYYY") : "-";

const FilterDateInput = (props: { onChange: (value: string) => void; value: string }) => {
  const [localValue, setLocalValue] = useState(props.value || "");

  useEffect(() => {
    setLocalValue(props.value || "");
  }, [props.value]);

  const handleChange = useCallback(
    (value: string) => {
      setLocalValue(value);

      props.onChange(BE_DATE_PATTERN.test(value) ? value : "");
    },
    [props.onChange],
  );

  return <DateTextBox placeholder="dd/mm/yyyy" value={localValue} onChange={handleChange} />;
};

const formatAmount = (value?: string | null) =>
  value === null || value === undefined || value === ""
    ? "-"
    : Number(value).toLocaleString(undefined, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      });

const formatRate = (value?: string | null) =>
  value === null || value === undefined || value === "" ? "-" : `${Number(value)}%`;

const CardAffiliateCommission = (props: CardAffiliateCommissionProps) => {
  const [activeTab, setActiveTab] = useState<TabName>("SEARCH");

  useEffect(() => {
    props.runSequence({ sequence: SEQUENCE_NAME, restart: true });

    return () => {
      props.runSequence({ sequence: SEQUENCE_NAME, clear: true });
    };
  }, []);

  const filter = useMemo(
    () => props.AffiliateCommissionSequence?.filter,
    [props.AffiliateCommissionSequence?.filter],
  );

  const commissionList = useMemo(
    () => props.AffiliateCommissionSequence?.commissionList || [],
    [props.AffiliateCommissionSequence?.commissionList],
  );

  const packageNameDict = useMemo(
    () => props.AffiliateCommissionSequence?.packageNameDict || {},
    [props.AffiliateCommissionSequence?.packageNameDict],
  );

  const totalPages = useMemo(
    () => Math.ceil((props.AffiliateCommissionSequence?.total || 0) / ROWS_PER_PAGE) || 1,
    [props.AffiliateCommissionSequence?.total],
  );

  const handleChangeFilter = useCallback(
    (key: keyof AffiliateCommissionFilterType, value: any) => {
      props.setProp(`AffiliateCommissionSequence.filter.${key}`, value);
    },
    [props.setProp],
  );

  const handleChangeDate = useCallback(
    (key: keyof AffiliateCommissionFilterType) => (date: string) => {
      handleChangeFilter(key, date);
    },
    [handleChangeFilter],
  );

  const handleSelectUser = useCallback(
    (value: any, key: any) => {
      handleChangeFilter("user", key || null);
    },
    [handleChangeFilter],
  );

  const handleSelectPatient = useCallback(
    (value: any, key: any) => {
      handleChangeFilter("patient", key || null);
    },
    [handleChangeFilter],
  );

  const handleSelectPackage = useCallback(
    (value: any, key: any) => {
      handleChangeFilter("package", key || null);
    },
    [handleChangeFilter],
  );

  const handleSearch = useCallback(() => {
    props.runSequence({ sequence: SEQUENCE_NAME, action: "SEARCH", page: 1 });
  }, [props.runSequence]);

  const handleClear = useCallback(() => {
    props.runSequence({ sequence: SEQUENCE_NAME, action: "CLEAR" });
  }, [props.runSequence]);

  const handleChangePage = useCallback(
    (event: any, data: any) => {
      props.runSequence({
        sequence: SEQUENCE_NAME,
        action: "SEARCH",
        page: Number(data.activePage),
      });
    },
    [props.runSequence],
  );

  const handleSelectTab = useCallback(
    (tab: { disabled: boolean; name: TabName }) => () => {
      if (!tab.disabled) {
        setActiveTab(tab.name);
      }
    },
    [],
  );

  const renderDateRange = (
    label: string,
    startKey: keyof AffiliateCommissionFilterType,
    endKey: keyof AffiliateCommissionFilterType,
  ) => (
    <div className="filter-group">
      <label>{label}</label>
      <div className="date-range">
        <FilterDateInput
          value={(filter?.[startKey] as string) || ""}
          onChange={handleChangeDate(startKey)}
        />
        <span>ถึง</span>
        <FilterDateInput
          value={(filter?.[endKey] as string) || ""}
          onChange={handleChangeDate(endKey)}
        />
      </div>
    </div>
  );

  return (
    <div className="CardAffiliateCommission" id={CARD_ID}>
      <div className="affiliate-commission-tabs">
        {TAB_ITEMS.map((tab) => (
          <div
            key={tab.name}
            className={`affiliate-commission-tab${activeTab === tab.name ? " active" : ""}${
              tab.disabled ? " disabled" : ""
            }`}
            onClick={handleSelectTab(tab)}
          >
            {tab.text}
          </div>
        ))}
      </div>

      {activeTab === "SEARCH" ? (
        <>
          <div className="affiliate-commission-filter">
            <div className="filter-row">
              {renderDateRange("วันที่ทำรายการ", "createdStart", "createdEnd")}
              {renderDateRange("วันที่ชำระเงิน", "paidStart", "paidEnd")}
              {renderDateRange("วันที่ใช้งาน Package", "performedStart", "performedEnd")}
            </div>

            <div className="filter-row">
              <div className="filter-group">
                <label>ผู้รับ Commission</label>
                <div className="filter-input">
                  <SearchBoxDropdown
                    onEvent={props.onEvent}
                    id={CARD_ID}
                    type="User"
                    fluid
                    icon="search"
                    limit={20}
                    placeholder="ค้นหาผู้รับ Commission"
                    searchedItemListWithKey={props.searchedItemListWithKey}
                    selectedItem={filter?.user || null}
                    setSelectedItem={handleSelectUser}
                    useWithKey
                  />
                </div>
              </div>

              <div className="filter-group">
                <label>ผู้รับบริการ</label>
                <div className="filter-input">
                  <SearchBoxDropdown
                    onEvent={props.onEvent}
                    id={CARD_ID}
                    type="Patient"
                    fluid
                    icon="search"
                    limit={20}
                    placeholder="ค้นหาผู้รับบริการ"
                    searchedItemListWithKey={props.searchedItemListWithKey}
                    selectedItem={filter?.patient || null}
                    setSelectedItem={handleSelectPatient}
                    useWithKey
                  />
                </div>
              </div>

              <div className="filter-group">
                <label>ชื่อ Package</label>
                <div className="filter-input">
                  <SearchBoxDropdown
                    onEvent={props.onEvent}
                    id={CARD_ID}
                    type="Package"
                    fluid
                    icon="search"
                    limit={20}
                    placeholder="ค้นหาชื่อ Package"
                    searchedItemListWithKey={props.searchedItemListWithKey}
                    selectedItem={filter?.package || null}
                    setSelectedItem={handleSelectPackage}
                    onAdditionalUrlParams={() => ({ include_affiliate_exclusive: true })}
                    useWithKey
                  />
                </div>
              </div>

              <div className="filter-group">
                <label>Status</label>
                <Dropdown
                  className="filter-input"
                  selection
                  options={COMMISSION_STATUS_OPTIONS}
                  value={filter?.commissionStatus || ""}
                  onChange={(event, data) => handleChangeFilter("commissionStatus", data.value)}
                />
              </div>

              <Button
                color="pink"
                loading={props.buttonLoadCheck?.[BTN_ACTS.SEARCH] === "LOADING"}
                onClick={handleSearch}
              >
                ค้นหา
              </Button>

              <Button basic onClick={handleClear}>
                ล้างค่า
              </Button>
            </div>
          </div>

          <ErrorMessage error={props.errorMessage?.[BTN_ACTS.SEARCH]} />

          <div className="affiliate-commission-table">
            <Table celled structured>
              <Table.Header>
                <Table.Row>
                  {TABLE_HEADERS.map((header) => (
                    <Table.HeaderCell key={header}>{header}</Table.HeaderCell>
                  ))}
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {commissionList.length === 0 ? (
                  <Table.Row>
                    <Table.Cell className="no-data-row" colSpan={TABLE_HEADERS.length}>
                      ไม่พบข้อมูล
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  commissionList.map((item: any) => (
                    <Table.Row key={item.id}>
                      <Table.Cell className="cell-center">
                        {formatDisplayDate(item.created_at)}
                      </Table.Cell>
                      <Table.Cell className="cell-center">
                        {formatDisplayDate(item.paid_at)}
                      </Table.Cell>
                      <Table.Cell className="cell-center">
                        {formatDisplayDate(item.performed_at)}
                      </Table.Cell>
                      <Table.Cell className="cell-center">{item.affiliate?.code || "-"}</Table.Cell>
                      <Table.Cell>
                        {item.affiliate?.name || item.affiliate?.user?.full_name || "-"}
                      </Table.Cell>
                      <Table.Cell>{item.patient_name || "-"}</Table.Cell>
                      <Table.Cell>
                        {item.package_name || packageNameDict[item.product] || "-"}
                      </Table.Cell>
                      <Table.Cell className="cell-right">{formatAmount(item.price)}</Table.Cell>
                      <Table.Cell className="cell-center">
                        {formatRate(item.commission_rate)}
                      </Table.Cell>
                      <Table.Cell className="cell-right">
                        {formatAmount(item.commission_amount)}
                      </Table.Cell>
                      <Table.Cell>{item.commission_status_label || "-"}</Table.Cell>
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table>
          </div>

          <div className="affiliate-commission-pagination">
            <Pagination
              activePage={props.AffiliateCommissionSequence?.activePage || 1}
              totalPages={totalPages}
              onPageChange={handleChangePage}
            />
          </div>
        </>
      ) : (
        activeTab === "SETTING" && (
          <CardAffiliateSettings
            runSequence={props.runSequence}
            setProp={props.setProp}
            onEvent={props.onEvent}
            searchedItemListWithKey={props.searchedItemListWithKey}
            AffiliateSettingsSequence={props.AffiliateSettingsSequence}
            masterData={props.masterData}
            buttonLoadCheck={props.buttonLoadCheck}
            languageUX={props.languageUX}
          />
        )
      )}
    </div>
  );
};

export default React.memo(CardAffiliateCommission);
